'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { FormSelect } from '@/components/forms/FormSelect';
import { BudgetInput } from '@/components/forms/BudgetInput';
import { BottomSheet } from '@/components/common/BottomSheet';
import { handleError } from '@/lib/utils/error';
import type { Team } from '@/lib/types/auction';
import { denominationMultipliers } from '@/lib/utils/money';
import { formatCurrency } from '@/lib/utils/format';

const iconAssignmentSchema = z.object({
  teamId: z.string().min(1, 'Team selection is required'),
  budgetAmount: z.coerce
    .number({ invalid_type_error: 'Amount is required' })
    .min(0, 'Amount cannot be negative'),
  budgetDenom: z.enum(['thousands', 'lacs', 'crores']),
});

type IconAssignmentInput = z.infer<typeof iconAssignmentSchema>;

interface IconAssignmentModalProps {
  teams: Team[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { teamId: string; amount: number }) => Promise<void>;
}

export function IconAssignmentModal({
  teams,
  isOpen,
  onClose,
  onSave,
}: IconAssignmentModalProps) {
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IconAssignmentInput>({
    resolver: zodResolver(iconAssignmentSchema),
    defaultValues: {
      teamId: '',
      budgetAmount: 0,
      budgetDenom: 'thousands',
    },
  });

  const onSubmit = async (data: IconAssignmentInput) => {
    try {
      setError(null);
      const amount =
        data.budgetAmount * denominationMultipliers[data.budgetDenom];

      // Validate team budget
      const selectedTeam = teams.find((team) => team.id === data.teamId);
      if (selectedTeam && amount > selectedTeam.budget) {
        throw new Error('Amount exceeds team budget');
      }

      await onSave({
        teamId: data.teamId,
        amount,
      });
      onClose();
    } catch (err) {
      setError(handleError(err, 'Icon Assignment'));
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Icon Player"
      className="sm:max-w-md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <FormSelect
          label="Select Team"
          name="teamId"
          register={register}
          error={errors.teamId?.message}
          options={[
            { value: '', label: 'Select Team' },
            ...teams.map((team) => ({
              value: team.id,
              label: `${team.name} (Budget: ${formatCurrency(team.budget - team.spent)})`,
            })),
          ]}
        />

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Assignment Amount
          </label>
          <BudgetInput
            amountName="budgetAmount"
            denomName="budgetDenom"
            register={register}
            amountError={errors.budgetAmount?.message}
            denomError={errors.budgetDenom?.message}
          />
          <p className="mt-1 text-xs text-gray-500">
            Leave as 0 for free icon assignment
          </p>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button variant="light" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Assign Icon
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}
